import React from 'react';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

interface PlanFeature {
  feature: string;
  support: boolean;
}

interface PlanContentProps {
  content: string | PlanFeature[];
  className?: string;
}

export const PlanContent: React.FC<PlanContentProps> = ({ content, className = '' }) => {
  // 如果content是字符串，尝试解析为JSON
  const parseContent = (content: string | PlanFeature[]): PlanFeature[] => {
    if (Array.isArray(content)) {
      return content;
    }
    
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      console.warn('Failed to parse plan content:', error);
    }
    
    // 如果解析失败，返回空数组
    return [];
  };

  const features = parseContent(content);

  if (features.length === 0) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        暂无套餐说明
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {features.map((item, index) => (
        <div
          key={index}
          className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex-shrink-0 mt-0.5">
            {item.support ? (
              <CheckCircleOutlined className="text-green-500 text-lg" />
            ) : (
              <CloseCircleOutlined className="text-red-500 text-lg" />
            )}
          </div>
          <div className="flex-1">
            <span className={`text-sm leading-relaxed ${
              item.support ? 'text-gray-700' : 'text-gray-500 line-through'
            }`}>
              {item.feature}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// 移动端优化版本
export const PlanContentMobile: React.FC<PlanContentProps> = ({ content, className = '' }) => {
  const parseContent = (content: string | PlanFeature[]): PlanFeature[] => {
    if (Array.isArray(content)) {
      return content;
    }
    
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      console.warn('Failed to parse plan content:', error);
    }
    
    return [];
  };

  const features = parseContent(content);

  if (features.length === 0) {
    return (
      <div className={`text-gray-500 text-xs text-center py-4 ${className}`}>
        暂无套餐说明
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {features.map((item, index) => (
        <div
          key={index}
          className="flex items-start gap-2 p-2 rounded-md bg-gray-50"
        >
          <div className="flex-shrink-0 mt-0.5">
            {item.support ? (
              <CheckCircleOutlined className="text-green-500 text-sm" />
            ) : (
              <CloseCircleOutlined className="text-red-500 text-sm" />
            )}
          </div>
          <div className="flex-1">
            <span className={`text-xs leading-relaxed ${
              item.support ? 'text-gray-700' : 'text-gray-500 line-through'
            }`}>
              {item.feature}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlanContent;